"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AuditLogEventSchema = exports.AuditLogStagingPayloadSchema = exports.toAuditLogResourceRef = exports.AUDIT_LOG_TRUNCATION_MARKER = exports.AUDIT_LOG_RESOURCE_REFS_MAX = exports.AUDIT_LOG_TEXT_MAX_BYTES = exports.AUDIT_LOG_PARAMS_MAX_BYTES = void 0;
const zod_1 = require("zod");
// Audit events are written to the Postgres `audit_log_staging` table and from
// there into the ClickHouse `audit_logs` table. A `change` event records a
// mutation with its before/after state. An `access` event records a read: the
// request params, the number of returned rows, and typed refs to the
// resources that were opened or returned.
/** Upper bound for the serialized `params` of an access event, in UTF-8 bytes. */
exports.AUDIT_LOG_PARAMS_MAX_BYTES = 2048;
/** Upper bound for `outcomeReason` and the User-Agent, in UTF-8 bytes. */
exports.AUDIT_LOG_TEXT_MAX_BYTES = 512;
/**
 * Upper bound for `resourceRefs` on one event: twice the largest list page
 * (1000 rows on GET /api/public/v2/observations), so a list can carry an
 * observation ref and a trace ref for every row it returned.
 */
exports.AUDIT_LOG_RESOURCE_REFS_MAX = 2000;
/** Ends a value that was cut to its byte limit. Cut `params` are no longer valid JSON. */
exports.AUDIT_LOG_TRUNCATION_MARKER = "...[truncated]";
const AUDIT_LOG_RESOURCE_REF_TYPES = [
    "trace",
    "observation",
    "score",
    "session",
];
// The type prefix keeps user-supplied ids of different resource types apart.
const AuditLogResourceRefSchema = zod_1.z.templateLiteral([
    zod_1.z.enum(AUDIT_LOG_RESOURCE_REF_TYPES),
    ":",
    zod_1.z.string().min(1),
]);
const toAuditLogResourceRef = (type, id) => `${type}:${id}`;
exports.toAuditLogResourceRef = toAuditLogResourceRef;
const AuditLogActorSchema = zod_1.z.discriminatedUnion("type", [
    zod_1.z.object({
        type: zod_1.z.literal("USER"),
        userId: zod_1.z.string().min(1),
        // Name and email as of the event, so removed members stay identifiable.
        name: zod_1.z.string().optional(),
        email: zod_1.z.string().optional(),
        orgRole: zod_1.z.string().optional(),
        projectRole: zod_1.z.string().optional(),
    }),
    zod_1.z.object({
        type: zod_1.z.literal("API_KEY"),
        apiKeyId: zod_1.z.string().min(1),
        // Set when the key acts on behalf of a user, e.g. an in-app agent key.
        userId: zod_1.z.string().optional(),
        name: zod_1.z.string().optional(),
        email: zod_1.z.string().optional(),
    }),
    zod_1.z.object({
        type: zod_1.z.literal("SYSTEM"),
        // The acting component, e.g. "stripe-webhook".
        name: zod_1.z.string().min(1),
    }),
]);
const AuditRequestContextSchema = zod_1.z.object({
    ip: zod_1.z.string().optional(),
    userAgent: zod_1.z.string().optional(),
    requestId: zod_1.z.string().optional(),
});
const AuditLogOutcomeSchema = zod_1.z.enum(["success", "denied", "error"]);
// Bounded by the UInt32 `result_count` column in ClickHouse.
const ResultCountSchema = zod_1.z.number().int().nonnegative().max(4_294_967_295);
const StagedEventBaseSchema = zod_1.z.object({
    orgId: zod_1.z.string().min(1),
    // Absent for organisation-level events.
    projectId: zod_1.z.string().optional(),
    actor: AuditLogActorSchema,
    resourceType: zod_1.z.string().min(1),
    // Absent for lists, which identify their rows through `resourceRefs`.
    resourceId: zod_1.z.string().optional(),
    resourceRefs: zod_1.z.array(AuditLogResourceRefSchema),
    action: zod_1.z.string().min(1),
    outcome: AuditLogOutcomeSchema,
    outcomeReason: zod_1.z.string().optional(),
    surface: zod_1.z.enum(["trpc", "public-api", "mcp"]).optional(),
    route: zod_1.z.string().optional(),
    request: AuditRequestContextSchema.optional(),
});
/** The `payload` of an `audit_log_staging` row. */
exports.AuditLogStagingPayloadSchema = zod_1.z.discriminatedUnion("kind", [
    StagedEventBaseSchema.extend({
        kind: zod_1.z.literal("change"),
        // Serialized JSON.
        before: zod_1.z.string().optional(),
        after: zod_1.z.string().optional(),
    }),
    StagedEventBaseSchema.extend({
        kind: zod_1.z.literal("access"),
        // Serialized JSON of the validated request input, at most
        // AUDIT_LOG_PARAMS_MAX_BYTES.
        params: zod_1.z.string().optional(),
        resultCount: ResultCountSchema.optional(),
    }),
]);
const utf8ByteLength = (char) => {
    const codePoint = char.codePointAt(0) ?? 0;
    if (codePoint <= 0x7f)
        return 1;
    if (codePoint <= 0x7ff)
        return 2;
    if (codePoint <= 0xffff)
        return 3;
    return 4;
};
const MARKER_BYTES = Array.from(exports.AUDIT_LOG_TRUNCATION_MARKER).reduce((sum, char) => sum + utf8ByteLength(char), 0);
// Cuts on a code point boundary, so the result never holds a lone surrogate,
// which Postgres rejects in jsonb.
const truncateUtf8 = (value, maxBytes) => {
    let bytes = 0;
    let index = 0;
    let cutAt;
    for (const char of value) {
        bytes += utf8ByteLength(char);
        if (cutAt === undefined && bytes > maxBytes - MARKER_BYTES)
            cutAt = index;
        if (bytes > maxBytes) {
            return value.slice(0, cutAt) + exports.AUDIT_LOG_TRUNCATION_MARKER;
        }
        index += char.length;
    }
    return value;
};
const cappedText = zod_1.z
    .string()
    .optional()
    .transform((value) => value === undefined
    ? undefined
    : truncateUtf8(value, exports.AUDIT_LOG_TEXT_MAX_BYTES));
const serializedJson = zod_1.z
    .unknown()
    .optional()
    .transform((value) => value === undefined || value === null ? undefined : JSON.stringify(value));
// Postgres jsonb cannot store NUL or an unpaired surrogate. Both become U+FFFD,
// so an event whose ids carry one still stages instead of failing the request.
// `before`, `after` and `params` are already escaped by JSON.stringify.
const toJsonbSafeStrings = (value) => {
    if (typeof value === "string") {
        // not a "\uFFFD" literal: utf8 bundlers inline it as a raw replacement char
        return value
            .replaceAll("\u0000", String.fromCharCode(0xfffd))
            .toWellFormed();
    }
    if (Array.isArray(value))
        return value.map(toJsonbSafeStrings);
    if (typeof value === "object" && value !== null) {
        return Object.fromEntries(Object.entries(value).map(([key, entry]) => [
            key,
            toJsonbSafeStrings(entry),
        ]));
    }
    return value;
};
const EventBaseSchema = StagedEventBaseSchema.extend({
    resourceRefs: zod_1.z
        .array(AuditLogResourceRefSchema)
        .default([])
        .transform((refs) => Array.from(new Set(refs)).slice(0, exports.AUDIT_LOG_RESOURCE_REFS_MAX)),
    outcome: AuditLogOutcomeSchema.default("success"),
    outcomeReason: cappedText,
    request: AuditRequestContextSchema.extend({
        userAgent: cappedText,
    }).optional(),
});
/**
 * An audit event as callers record it. Parsing serializes `before`, `after`
 * and `params` to JSON, applies the size caps above and makes every string
 * storable in jsonb, which yields an `AuditLogStagingPayload`.
 */
exports.AuditLogEventSchema = zod_1.z
    .discriminatedUnion("kind", [
    EventBaseSchema.extend({
        kind: zod_1.z.literal("change"),
        before: serializedJson,
        after: serializedJson,
    }),
    EventBaseSchema.extend({
        kind: zod_1.z.literal("access"),
        params: serializedJson.transform((json) => json === undefined
            ? undefined
            : truncateUtf8(json, exports.AUDIT_LOG_PARAMS_MAX_BYTES)),
        resultCount: ResultCountSchema.optional(),
    }),
])
    .transform((event) => toJsonbSafeStrings(event));
